import { formatLogonId } from '@/data/utils/formatLogonId';

export type StoredLoginMethod = 'password' | 'email' | 'phone';

const LOGIN_LAST_METHOD_KEY = 'login-last-method:v1';

const isStoredLoginMethod = (value: unknown): value is StoredLoginMethod =>
	value === 'password' || value === 'email' || value === 'phone';

const readStorageMap = (): Record<string, StoredLoginMethod> => {
	if (typeof window === 'undefined') return {};

	const raw = localStorage.getItem(LOGIN_LAST_METHOD_KEY);
	if (!raw) return {};

	try {
		const parsed = JSON.parse(raw) as Record<string, unknown>;

		return Object.entries(parsed).reduce<Record<string, StoredLoginMethod>>((acc, [key, value]) => {
			if (isStoredLoginMethod(value)) {
				acc[key] = value;
			}

			return acc;
		}, {});
	} catch {
		localStorage.removeItem(LOGIN_LAST_METHOD_KEY);
		return {};
	}
};

const writeStorageMap = (data: Record<string, StoredLoginMethod>) => {
	if (typeof window === 'undefined') return;

	if (Object.keys(data).length === 0) {
		localStorage.removeItem(LOGIN_LAST_METHOD_KEY);
		return;
	}

	localStorage.setItem(LOGIN_LAST_METHOD_KEY, JSON.stringify(data));
};

export const readStoredLoginMethod = (logonId: string): StoredLoginMethod | undefined => {
	const normalizedLogonId = formatLogonId(logonId);
	if (!normalizedLogonId) return undefined;

	return readStorageMap()[normalizedLogonId];
};

export const writeStoredLoginMethod = (logonId: string, method: StoredLoginMethod) => {
	const normalizedLogonId = formatLogonId(logonId);
	if (!normalizedLogonId || typeof window === 'undefined') return;

	const data = readStorageMap();
	data[normalizedLogonId] = method;
	writeStorageMap(data);
};

export const removeStoredLoginMethod = (logonId: string) => {
	const normalizedLogonId = formatLogonId(logonId);
	if (!normalizedLogonId || typeof window === 'undefined') return;

	const data = readStorageMap();
	if (!(normalizedLogonId in data)) return;

	delete data[normalizedLogonId];
	writeStorageMap(data);
};
